// 커뮤니티·고객센터 진입점(D5b, docs/COMMUNITY-SUPPORT-PRD.md §1·§9). main.ts가 startCommunity를 한 번 부른다(게임 루프 밖).
// - 신원: D5a 계정 쿠키(identity 'account'). 계정이 준비되면 연결하고, 계정이 바뀌면(Google 전환·삭제) 다시 맞춘다.
// - 화면: 마을에서만 오른쪽 가장자리 커뮤니티 버튼(미확인 배지) → 채팅 시트·친구·게시판·고객센터.
//   던전·결과 화면은 전투 모드: 패널을 닫고 왼쪽 가장자리 띠(최대 2줄)·배지만(대표님 규칙 — 플레이 영역을 가리지 않는다).
// - 장면 확인은 0.4초 간격 타이머(렌더·고정 60Hz 루프 밖). 실패는 모두 삼킨다(커뮤니티는 게임 진행을 막지 않는다).
import { RPG_WINDOW_EVENT } from '../ui/rpgWindowEvent';
import { createSocialClient, type SocialClient } from '../../packages/tll-social/src/client/index.js';
import { createCommunityUI, type CommunityHooks as SocialUiHooks, type CommunityUI, type Rect } from '../../packages/tll-social/src/ui/index.js';
import { accountClient } from '../account/index';
import { C, FONT, el } from '../ui/dom';
import { viewH, viewRect, viewW } from '../shared/viewport';
import { HUD_ICON_PX, iconOver } from '../ui/uiArt';
import { recentErrors } from '../telemetry/collector';
import { buildDiagnostics } from './diagnostics';
import { COMMUNITY_TABS, GAME_ID, LEGAL_LINKS, SOCIAL_CONFIG } from './config';

export type SceneName = 'title' | 'village' | 'dungeon' | 'result' | 'defeat' | 'boot';
export interface CommunityHooks {
  scene(): SceneName;
  /** 렌더러 이름(진단용, 선택) */
  renderer?(): string;
}
export interface Community {
  client: SocialClient;
  ui: CommunityUI;
  /** 고객센터 열기(설정 창 버튼). 던전 안이면 안내만 */
  openSupport(): boolean;
  /** 채팅 열기 */
  openChat(): boolean;
}

const THEME: Record<string, string> = {
  panel: '#121a30', panel2: C.navy, panel3: '#26335a', border: 'rgba(242,193,78,.5)',
  accent: '#e0a52f', accent2: '#ffd877', accentInk: '#2a1d05', gold: C.gold, blue: '#2f78e0', blue2: C.teal,
  text: '#f3f1ea', muted: '#9fb0d8', font: FONT,
};
const BUTTON = 46;
const PANEL_SCENES = new Set<SceneName>(['village', 'title']);

let active: Community | null = null;

/** 화면에 보이는 HUD 요소(버튼·testid) 영역 — 커뮤니티 버튼·전투 띠가 피할 장애물 */
function hudRects(skip: Element | null): Rect[] {
  const w = viewW(), h = viewH(), out: Rect[] = [];
  const nodes = document.querySelectorAll('#ui button, #ui [data-testid]');
  for (const n of nodes) {
    if (skip && skip.contains(n)) continue;
    const r = viewRect(n);
    if (r.width < 2 || r.height < 2 || r.right <= 0 || r.bottom <= 0 || r.left >= w || r.top >= h) continue;
    if (r.width * r.height > w * h * 0.4) continue; // 전체 덮개(설정 창 배경 등)는 장애물이 아니다
    const style = getComputedStyle(n as HTMLElement);
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) continue;
    out.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  }
  return out;
}
/** 장애물 영역 모음의 지문(정수 반올림) — 바뀌었는지만 비교한다 */
export function hudSignature(rects: Rect[]): string {
  return rects.map((r) => [r.left, r.top, r.right, r.bottom].map(Math.round).join(',')).join('|');
}
const overlaps = (a: Rect, b: Rect) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/** 오른쪽 가장자리에서 HUD와 겹치지 않는 첫 자리(세로 46% → 아래·위로 훑기) */
export function placeButton(w: number, h: number, obstacles: Rect[], size = BUTTON, inset = 10): { left: number; top: number } {
  const left = w - size - inset;
  const fits = (top: number) => {
    const r = { left, top, right: left + size, bottom: top + size };
    return top >= inset && r.bottom <= h - inset && !obstacles.some((o) => overlaps(r, { left: o.left - 4, top: o.top - 4, right: o.right + 4, bottom: o.bottom + 4 }));
  };
  const start = Math.round(h * 0.46 - size / 2);
  for (let d = 0; d < h; d += 6) {
    if (fits(start + d)) return { left, top: start + d };
    if (fits(start - d)) return { left, top: start - d };
  }
  return { left, top: Math.max(inset, Math.min(h - size - inset, start)) };
}

/**
 * 상점·대장간·가방 같은 마을 창(RpgUi)이 열려 있는지. 열려 있으면 커뮤니티 단추를 접는다 — 창 오른쪽 가장자리에 걸려
 * 구매·강화 단추를 반쯤 가리던 문제(2026-10-27 대표님 화면). 창을 닫으면 곧바로 돌아온다
 */
export function rpgWindowOpen(doc: Pick<Document, 'querySelector'> = document): boolean {
  // 캐릭터 선택·생성 화면도 같은 창으로 본다(작은 화면에서 목록 카드 오른쪽 끝을 가리던 문제, 2026-10-28)
  const b = doc.querySelector<HTMLElement>('[data-testid="rpg-backdrop"]') ?? null;
  if (b && b.style.display !== 'none' && b.getBoundingClientRect().width > 0) return true;
  return !!doc.querySelector('[data-testid="character-screen"][data-open="1"]');
}

/** 커뮤니티 UI가 붙을 곳 — #app 안(세로 폰에서 #app이 90도 돌면 같이 돈다, RG-022). #app이 없으면 body */
function appHost(): HTMLElement {
  return document.getElementById('app') ?? document.body;
}

function makeButton(): { root: HTMLButtonElement; badge: HTMLSpanElement } {
  const b = document.createElement('button');
  b.type = 'button';
  b.dataset.testid = 'community-open';
  b.setAttribute('aria-label', '커뮤니티 · 채팅·게시판·고객센터');
  b.style.cssText = 'position:fixed;z-index:60;width:' + BUTTON + 'px;height:' + BUTTON + 'px;border-radius:50%;display:none;align-items:center;justify-content:center;' +
    'pointer-events:auto;cursor:pointer;border:1px solid rgba(255,255,255,.32);background:rgba(10,16,32,.55);padding:0;touch-action:manipulation;';
  // HUD 정리(2026-10-27): 얇은 둥근 단추 + Codex image_gen 말풍선 그림(btn-community). 받지 못하면 선 말풍선 SVG가 대신 보인다(PR #211 리뷰 P1)
  iconOver(el('div', 'width:' + HUD_ICON_PX + 'px;height:' + HUD_ICON_PX + 'px;display:grid;place-items:center;pointer-events:none;', b), 'btn-community', HUD_ICON_PX,
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="#f4ecd8" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16v11H9l-5 4z"/></svg>');
  const badge = document.createElement('span');
  badge.dataset.testid = 'community-badge';
  badge.style.cssText = 'position:absolute;right:-4px;top:-4px;min-width:18px;height:18px;padding:0 4px;box-sizing:border-box;border-radius:9px;background:' + C.crimson + ';color:#fff;font:900 11px/18px ' + FONT + ';text-align:center;display:none;';
  b.appendChild(badge);
  appHost().appendChild(b);
  return { root: b, badge };
}

function hint(text: string): void {
  const d = document.createElement('div');
  d.dataset.testid = 'community-hint';
  d.textContent = text;
  // 위 가장자리 띠(플레이 영역 가운데를 가리지 않는다)
  d.style.cssText = 'position:fixed;left:50%;top:calc(env(safe-area-inset-top,0px) + 6px);transform:translateX(-50%);z-index:80;pointer-events:none;padding:6px 14px;border-radius:999px;' +
    'background:rgba(10,14,30,.88);border:1px solid rgba(242,193,78,.6);color:#f3f1ea;font:700 13px ' + FONT + ';white-space:nowrap;';
  appHost().appendChild(d);
  setTimeout(() => d.remove(), 2600);
}

/** 부팅 뒤 한 번(게임 루프 밖). 실패하면 null — 게임은 그대로 */
export function startCommunity(hooks: CommunityHooks): Community | null {
  if (active) return active;
  try {
    const client = createSocialClient({ gameId: GAME_ID, identity: 'account', presence: { state: 'lobby' } });
    const btn = makeButton();
    const diagnostics = () => buildDiagnostics({
      scene: safeScene(hooks), ua: navigator.userAgent, width: viewW(), height: viewH(),
      dpr: window.devicePixelRatio || 1, recent: recentErrors(), renderer: hooks.renderer?.(),
    });
    const uiHooks: SocialUiHooks = {
      canOpenPanel: () => PANEL_SCENES.has(safeScene(hooks)),
      legalLinks: [...LEGAL_LINKS],
      diagnostics,
      hudObstacles: () => hudRects(btn.root),
      viewSize: () => ({ w: viewW(), h: viewH() }),
      viewRect: (n) => viewRect(n),
      hudBottom: () => {
        const h = viewH(), w = viewW();
        const top = hudRects(btn.root).filter((r) => r.top < h * 0.4 && r.left < w / 3);
        return top.length ? Math.max(...top.map((r) => r.bottom)) : Math.round(h * 0.2);
      },
      controlsTop: () => {
        const h = viewH();
        const low = hudRects(btn.root).filter((r) => r.top > h * 0.5);
        return low.length ? Math.min(...low.map((r) => r.top)) : Math.round(h * 0.62);
      },
    };
    const ui = createCommunityUI({ client, mount: appHost(), config: SOCIAL_CONFIG, theme: THEME, tabs: [...COMMUNITY_TABS], hooks: uiHooks });
    // 공개 디스코드 초대 링크(서버 vars DISCORD_INVITE): 받아 오면 고객센터에 '디스코드 참여' 버튼이 생긴다. 실패·없음이면 버튼 없음
    void fetch('/api/community/config', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { discordInvite?: unknown } | null) => { if (j && typeof j.discordInvite === 'string') uiHooks.discordInvite = j.discordInvite; })
      .catch(() => { /* 초대 링크는 선택 */ });

    // 버튼: 마을에서만, 자리는 화면 크기·장면이 바뀔 때만 다시 잰다
    let lastScene: SceneName | '' = '';
    let lastSize = '';
    let lastHud = '';
    const place = () => {
      const p = placeButton(viewW(), viewH(), hudRects(btn.root));
      btn.root.style.left = p.left + 'px';
      btn.root.style.top = p.top + 'px';
    };
    btn.root.addEventListener('click', () => { ui.toggle('chat'); });
    ui.on('badge', (n: number) => {
      btn.badge.style.display = n > 0 ? 'block' : 'none';
      btn.badge.textContent = n > 99 ? '99+' : String(n);
    });
    ui.on('open', (open: boolean) => { btn.root.style.opacity = open ? '0' : '1'; btn.root.style.pointerEvents = open ? 'none' : 'auto'; });
    const tick = () => {
      try {
        const scene = safeScene(hooks);
        const size = viewW() + 'x' + viewH();
        if (scene !== lastScene) {
          lastScene = scene;
          const battle = !PANEL_SCENES.has(scene);
          ui.setBattle(battle);
          client.setPresence(battle ? { state: 'battle' } : { state: 'lobby', where: scene === 'village' ? '마을' : '타이틀' });
          btn.root.style.display = scene === 'village' ? 'flex' : 'none';
          lastSize = '';
          lastHud = '';
        }
        // HUD가 늦게 그려질 수 있어 마을에서는 크기가 같아도 3초마다 한 번 다시 잰다.
        // 포털 정보 카드(region-card)처럼 나중에 나타나거나 커지는 HUD도 바로 비켜 가도록(RG-015) 장애물 영역이 바뀌면 곧바로 다시 놓는다
        if (scene === 'village') {
          const sig = hudSignature(hudRects(btn.root));
          if (size !== lastSize || sig !== lastHud || ++settle % 8 === 0) { lastSize = size; lastHud = sig; place(); }
        }
      } catch { /* 무시 */ }
    };
    let settle = 0;
    // 마을 창이 열려 있으면 단추를 접는다(창이 열리고 닫힐 때 오는 이벤트로만 — 폴링 없음)
    let folded = false;
    const syncFold = () => {
      try {
        const want = rpgWindowOpen();
        if (want !== folded) { folded = want; btn.root.style.visibility = want ? 'hidden' : 'visible'; }
      } catch { /* 무시 */ }
    };
    window.addEventListener(RPG_WINDOW_EVENT, syncFold);
    syncFold();
    setInterval(tick, 400);
    tick();

    // 계정(D5a) 준비·변경에 맞춰 커뮤니티 신원을 맞춘다
    let accountId: string | null = null;
    accountClient().onChange((s) => {
      if (s.accountId === accountId) return;
      const had = accountId !== null;
      accountId = s.accountId;
      if (!accountId) { ui.close(); client.disconnect(); return; }
      if (had) client.identityChanged();
      void ui.start();
    });
    // 자가 회복(10초마다): 계정은 있는데 커뮤니티가 끊긴 채 멈춰 있으면 다시 붙인다.
    // 클라우드 E2E에서 던전 부팅 중 /me는 성공했는데 소켓을 한 번도 열지 않은 채 남는 경우가 드물게 있었다(원인 미확정 — 재연결 타이머·가시성 경합 후보)
    setInterval(() => {
      try {
        if (!accountId) return;
        if (ui.phase === 'ready' && client.connection === 'offline') client.connect();
        else if (ui.phase === 'idle' || ui.phase === 'offline') void ui.start();
      } catch { /* 무시 */ }
    }, 10_000);

    active = {
      client, ui,
      openSupport() {
        if (!PANEL_SCENES.has(safeScene(hooks))) { hint('고객센터는 마을에서 열 수 있어요'); return false; }
        return ui.open('support');
      },
      openChat() { return ui.open('chat'); },
    };
    if (new URLSearchParams(location.search).has('qa')) (window as unknown as { __DH_COMMUNITY__: Community }).__DH_COMMUNITY__ = active;
    return active;
  } catch {
    return null;
  }
}

function safeScene(hooks: CommunityHooks): SceneName {
  try { return hooks.scene(); } catch { return 'boot'; }
}

/** 설정 창 「고객센터」 버튼이 부른다(커뮤니티가 아직 없으면 false) */
export function openSupport(): boolean {
  return active ? active.openSupport() : false;
}
